import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, KeyRound } from 'lucide-react';
import { config } from '@/config';
import AnimatedBackground from '@/components/AnimatedBackground';

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [token, setToken] = useState<string>('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const tokenFromUrl = searchParams.get('token');
        if (!tokenFromUrl) {
            setErrorMessage('Invalid or missing reset token');
            return;
        }
        setToken(tokenFromUrl);
    }, [searchParams]);

    const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrorMessage('');

        if (newPassword !== confirmPassword) {
            setErrorMessage('Passwords do not match');
            return;
        }

        try {
            setIsLoading(true);
            await axios.post(`${config.backendUrl}/reset-password`, {
                token,
                newPassword
            });
            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (error: any) {
            console.error('Password reset failed:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to reset password');
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
                <AnimatedBackground />
                <div className="max-w-md w-full space-y-8 p-10 rounded-xl bg-white/90 backdrop-blur-sm shadow-xl">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-amber-500">
                        Password Reset Successfully
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                        Redirecting to login page...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            <AnimatedBackground />
            <div className="max-w-md w-full space-y-8 p-10 rounded-xl bg-white/90 backdrop-blur-sm shadow-xl transform transition-all duration-300 hover:shadow-2xl">
                <div className="text-center">
                    <h2 className="mt-6 text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-800 to-amber-500">
                        Reset Your Password
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Enter a new password for your account
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
                    <div className="rounded-md space-y-4">
                        <div className="group">
                            <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 group-focus-within:border-slate-800 group-focus-within:ring-1 group-focus-within:ring-amber-400 transition-all duration-200">
                                <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-slate-800 transition-colors" />
                                <input
                                    id="new-password"
                                    name="newPassword"
                                    type="password"
                                    required
                                    className="flex-1 outline-none text-gray-900 placeholder-gray-500"
                                    placeholder="Create your password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    disabled={isLoading || !token}
                                />
                            </div>
                        </div>
                        <div className="group">
                            <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 group-focus-within:border-slate-800 group-focus-within:ring-1 group-focus-within:ring-amber-400 transition-all duration-200">
                                <KeyRound className="h-5 w-5 text-gray-400 group-focus-within:text-slate-800 transition-colors" />
                                <input
                                    id="confirm-password"
                                    name="confirmPassword"
                                    type="password"
                                    required
                                    className="flex-1 outline-none text-gray-900 placeholder-gray-500"
                                    placeholder="Confirm your password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    disabled={isLoading || !token}
                                />
                            </div>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="text-center text-red-500 text-sm animate-shake">
                            {errorMessage}
                        </div>
                    )}

                    <div>
                        <button
                            type="submit"
                            disabled={isLoading || !token}
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-900 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400 transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                <Lock className="h-5 w-5 text-amber-400 group-hover:text-amber-300 transition-colors" />
                            </span>
                            {isLoading ? 'Resetting Password...' : 'Reset Password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPasswordPage; 
